// The frame around every signed-in page: sidebar for the current area, top bar with search (Ctrl+K),
// area switcher and account menu.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AREA_INFO, type Area } from '@briary/shared'
import { can, initials, useAuth } from '../lib/auth'
import { api } from '../lib/api'
import { Link, useRouter } from '../lib/router'
import { navFor } from '../modules'
import { Icon } from '../ui'
import { CommandPalette } from './CommandPalette'
import { PortalAppearanceControl,usePortalAppearance } from '../modules/portal/appearance'
import { canOpenPortalPath } from '../lib/portal-modules'
import { canOpenStaffPath } from '../lib/staff-modules'
import { canOpenSensitiveMoneyPath } from '../lib/sensitive-money-access'
import { withPortalModuleLinks } from '../modules/portal/extra-modules'
import { withArchiveLink } from '../modules/legacy-archive/entry'
import {HelpButton} from '../modules/help/HelpButton'

export function Shell({ area, children }: { area: Area; children: ReactNode }) {
  const { me } = useAuth()
  const { loc } = useRouter()
  const [sideOpen, setSideOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const portalAppearance=usePortalAppearance(area==='portal')
  const classic=area==='portal'&&portalAppearance.layout==='classic'
  const groups = useMemo(() => withArchiveLink(withPortalModuleLinks(navFor(area, (p) => can(me, p)), area), area, me?.isSuperadmin === true).map(group => ({ ...group, items: group.items.filter(item => (area !== 'portal' || canOpenPortalPath(me, item.path)) && canOpenStaffPath(me, item.path) && canOpenSensitiveMoneyPath(me, item.path)) })).filter(group => group.items.length), [area, me])

  useEffect(() => { setSideOpen(false) }, [loc.path])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const isActive = (path: string) =>
    (path === '/admin/projects' && (loc.path === '/admin/project-register' || loc.path.startsWith('/admin/project-register/')))
    || (path === `/${area}` ? loc.path === path : loc.path === path || loc.path.startsWith(`${path}/`))

  return (
    <div className={`shell${sideOpen ? ' side-open' : ''}${classic?' portal-classic':''}`}>
      <aside className="side" aria-label="Main navigation">
        <Link to={`/${area}`} className="side-brand" aria-label="Briary Hub home">
          <img src={classic?'/brand/briary-logo.png':'/brand/briary-logo-white.png'} alt="Briary" />
          <span className="area-tag">{area === 'admin' ? 'Hub' : area === 'portal' ? 'Portal' : 'Subcon'}</span>
        </Link>
        {classic&&me&&<div className="classic-manager-panel"><span className="avatar">{initials(me.name,me.email)}</span><span>{me.name||me.email}</span></div>}
        <nav className="side-nav">
          {groups.map((g) => (
            <div className="side-group" key={g.label}>
              <div className="side-group-label">{g.label}</div>
              {g.items.map((item) =>
                item.soon ? (
                  <span key={item.path} className="side-link" style={{ opacity: 0.5, cursor: 'default' }} title="Coming in a later phase">
                    <Icon name={item.icon} />
                    {classic&&item.path==='/portal/sites'?'Project List':item.label}
                    <span className="soon">Soon</span>
                  </span>
                ) : (
                  <Link key={item.path} to={item.path} className={`side-link${isActive(item.path) ? ' active' : ''}`}>
                    <Icon name={item.icon} />
                    {classic&&item.path==='/portal/sites'?'Project List':item.label}
                  </Link>
                ),
              )}
            </div>
          ))}
        </nav>
        <div className="side-foot">Briary Hub · {area==='portal'?'Client portal':'v2'}</div>
      </aside>

      <div className="main">
        <header className="top">
          <button className="btn btn-ghost side-toggle" aria-label="Open menu" onClick={() => setSideOpen(true)}>
            <Icon name="menu" width={18} height={18} />
          </button>
          <button className="top-search" onClick={() => setPaletteOpen(true)} aria-label="Search">
            <Icon name="search" />
            <span>Search plots, projects, companies…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <div className="spacer" />
          {area==='portal'&&<PortalAppearanceControl/>}
          {me && me.areas.length > 1 && <AreaSwitcher current={area} areas={me.areas} />}
          <UserMenu />
        </header>
        <SystemBanner area={area} />
        <main id="content">{children}</main>
      </div>

      {sideOpen && <div className="modal-backdrop" style={{ zIndex: 35, padding: 0 }} onClick={() => setSideOpen(false)} />}
      {paletteOpen && <CommandPalette area={area} onClose={() => setPaletteOpen(false)} />}
      <HelpButton area={area}/>
    </div>
  )
}

function SystemBanner({ area }: { area: Area }) {
  const { data } = useQuery({ queryKey: ['system'], queryFn: () => api.get<{ mode: 'preview' | 'parallel' | 'live'; banner: string }>('/api/system'), staleTime: 60_000 })
  if (!data) return null
  return (
    <>
      {data.banner && <div className="sysbar sysbar-info">{data.banner}</div>}
      {data.mode!=='live'&&area==='portal'&&<div className="sysbar sysbar-info">Portal preview. Please keep using your current portal and SiteScope for live requests until Briary confirms the move.</div>}
      {data.mode === 'preview' && area === 'admin' && (
        <div className="sysbar">
          <strong>Preview</strong> — the current hub is still the master copy; this one is refreshed from it. Make changes there until switch day.
        </div>
      )}
      {data.mode === 'parallel' && area === 'admin' && (
        <div className="sysbar">
          <strong>Editing here</strong> — changes made here stay here (the current hub doesn’t see them); everything else is still copied in from the current hub.
        </div>
      )}
    </>
  )
}

function useClickAway(onAway: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && onAway()
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onAway])
  return ref
}

function AreaSwitcher({ current, areas }: { current: Area; areas: Area[] }) {
  const [open, setOpen] = useState(false)
  const { navigate } = useRouter()
  const ref = useClickAway(() => setOpen(false))
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="btn btn-sm" onClick={() => setOpen((o) => !o)}>
        <Icon name="swap" width={14} height={14} />
        {AREA_INFO[current].label}
      </button>
      {open && (
        <div className="menu">
          {areas.map((a) => (
            <button
              key={a}
              className="menu-item"
              onClick={async () => {
                setOpen(false)
                await api.post('/api/auth/area', { area: a }).catch(() => {})
                navigate(`/${a}`)
              }}
            >
              <Icon name={a === 'admin' ? 'building' : a === 'portal' ? 'folder' : 'wind'} />
              <span>
                {AREA_INFO[a].label}
                {a === current && <span className="muted"> · current</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function UserMenu() {
  const { me, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useClickAway(() => setOpen(false))
  if (!me) return null
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="btn btn-ghost" style={{ padding: 4, height: 40 }} onClick={() => setOpen((o) => !o)} aria-label="Account menu">
        <span className="avatar">{initials(me.name, me.email)}</span>
      </button>
      {open && (
        <div className="menu">
          <div className="menu-head">
            <strong>{me.name || me.email}</strong>
            <div className="muted" style={{ fontSize: 12 }}>
              {me.email}
            </div>
          </div>
          <Link to="/account" className="menu-item" onClick={() => setOpen(false)}>
            <Icon name="shield" /> Account & security
          </Link>
          <button className="menu-item" onClick={() => void signOut()}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}
